import React from "react";
import Link from "next/link";
import Image from "next/image";
import { BlogPostCard as BlogPostCardType } from "@/types/blog";

interface BlogPostCardProps {
  post: BlogPostCardType;
}

const BlogPostCard: React.FC<BlogPostCardProps> = ({ post }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <article className="bg-white rounded-xl border border-grey-2 overflow-hidden hover:shadow-lg transition-shadow duration-300">
      <Link href={`/blog/${post.slug}`}>
        <div className="relative w-full h-48 md:h-56 lg:h-64 overflow-hidden">
          <Image
            src={post.featuredImage.url}
            alt={post.featuredImage.alt}
            fill
            className="object-cover hover:scale-105 transition-transform duration-300"
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        </div>
      </Link>

      <div className="p-5 md:p-6 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Link
            href={`/blog/category/${post.category.slug}`}
            className="inline-block"
          >
            <span className="text-xs font-semibold text-main-900 bg-main-100 px-3 py-1 rounded-full hover:bg-main-200 transition-colors">
              {post.category.name}
            </span>
          </Link>
          <span className="text-xs text-grey-7 font-medium">
            {post.readingTime} min read
          </span>
        </div>

        <Link href={`/blog/${post.slug}`}>
          <h3 className="text-xl md:text-2xl font-bold text-main-900 hover:text-main-700 transition-colors line-clamp-2">
            {post.title}
          </h3>
        </Link>

        <p className="text-grey-7 text-sm md:text-base line-clamp-3">
          {post.excerpt}
        </p>

        <div className="flex items-center justify-between pt-3 border-t border-grey-2 mt-auto">
          <div className="flex items-center gap-2">
            {post.author.avatar && (
              <div className="relative w-8 h-8 rounded-full overflow-hidden">
                <Image
                  src={post.author.avatar}
                  alt={post.author.name}
                  fill
                  className="object-cover"
                  sizes="32px"
                />
              </div>
            )}
            <div className="flex flex-col">
              <span className="text-sm font-medium text-main-900">
                {post.author.name}
              </span>
              <span className="text-xs text-grey-7">
                {formatDate(post.publishedAt)}
              </span>
            </div>
          </div>

          {post.views !== undefined && post.views > 0 && (
            <div className="flex items-center gap-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-grey-7"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <span className="text-xs text-grey-7">{post.views}</span>
            </div>
          )}
        </div>

        <Link href={`/blog/${post.slug}`}>
          <button className="mt-2 w-full bg-main-900 text-white py-2.5 px-4 rounded-lg font-semibold text-sm hover:bg-main-700 transition-colors">
            Read More
          </button>
        </Link>
      </div>
    </article>
  );
};

export default BlogPostCard;
